import React, { useState } from "react";
import { Sparkles, ArrowRight } from "lucide-react";
import { postAdvisor } from "../../api";
import { fmtPrice } from "../../lib/format";
import { toast } from "sonner";

const TFS = ["M15", "H1", "H4"];
const RISKS = [
  { id: "Conservativo", lot: "0.10", testid: "risk-profile-conservativo" },
  { id: "Bilanciato", lot: "0.25", testid: "risk-profile-bilanciato" },
  { id: "Aggressivo", lot: "0.50", testid: "risk-profile-aggressivo" },
];

export default function Advisor({ onApply, canTrade = true }) {
  const [tf, setTf] = useState("H1");
  const [risk, setRisk] = useState("Bilanciato");
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState(null);

  const generate = async () => {
    setBusy(true);
    setSetup(null);
    try {
      const res = await postAdvisor({ timeframe: tf, riskProfile: risk });
      setSetup(res);
      toast.success("Setup XAUUSD consigliato dall'AI");
    } catch (_) {
      toast.error("Advisor non disponibile");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id="segnali" data-testid="ai-signal-advisor-widget" className="panel p-5 space-y-4 float-in">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        <span className="w-6 h-6 rounded-md grid place-items-center bg-amber-500/15 text-amber-400">
          <Sparkles size={13} />
        </span>
        AI Signal Advisor · Oro
      </div>

      <div>
        <div className="text-[10px] text-slate-500 mb-1.5">Timeframe</div>
        <div className="flex gap-1 bg-[#0F1728] border border-white/10 rounded-lg p-1">
          {TFS.map((t) => (
            <button
              key={t}
              onClick={() => setTf(t)}
              className={`flex-1 py-1.5 rounded-md text-[11px] font-mono font-semibold transition-colors ${
                tf === t ? "bg-amber-500/20 text-amber-400" : "text-slate-500 hover:text-slate-200"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] text-slate-500 mb-1.5">Profilo di rischio</div>
        <div className="grid grid-cols-3 gap-2">
          {RISKS.map((r) => (
            <button
              key={r.id}
              data-testid={r.testid}
              onClick={() => setRisk(r.id)}
              className={`py-2 rounded-lg text-[10px] font-semibold border transition-colors ${
                risk === r.id
                  ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                  : "border-white/10 bg-[#0F1728] text-slate-400 hover:text-slate-200"
              }`}
            >
              {r.id}
              <span className="block font-mono text-[9px] text-slate-500 mt-0.5">Lot {r.lot}</span>
            </button>
          ))}
        </div>
      </div>

      <button
        data-testid="generate-advisor-signal-button"
        onClick={generate}
        disabled={busy}
        className="w-full py-2.5 rounded-lg text-[12px] font-bold text-[#0B0E17] bg-gradient-to-br from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 transition-colors disabled:opacity-60"
      >
        {busy ? "Analisi in corso…" : "✦ Genera segnale consigliato"}
      </button>

      {setup && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3.5 space-y-3 float-in">
          <div className="flex items-center justify-between">
            <span
              className="font-mono font-extrabold text-[13px]"
              style={{ color: setup.side === "BUY" ? "#10B981" : "#EF4444" }}
            >
              {setup.decisionLabel} XAUUSD
            </span>
            <span className="text-[10px] font-mono text-sky-300">
              {Math.round(setup.confidence * 100)}% conf · R/R {setup.rr}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[10px] font-mono">
            <Level label="OPEN" value={`${fmtPrice(setup.openLow)}–${fmtPrice(setup.openHigh)}`} />
            <Level label="SL" value={fmtPrice(setup.sl)} danger />
            {setup.tps.map((t, i) => (
              <Level key={i} label={`TP${i + 1}`} value={fmtPrice(t)} good />
            ))}
          </div>
          <p className="text-[10px] text-slate-400 leading-relaxed border-t border-white/8 pt-2">{setup.reason}</p>
          <button
            data-testid="apply-advisor-signal-button"
            onClick={() => onApply(setup)}
            disabled={!canTrade}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-bold text-slate-100 bg-[#1a2740] border border-white/10 hover:bg-[#22314f] transition-colors disabled:opacity-40"
          >
            Invia a MT5 Bridge <ArrowRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

function Level({ label, value, good, danger }) {
  return (
    <span className="flex justify-between gap-2">
      <span className={good ? "text-emerald-400/80" : danger ? "text-rose-400/80" : "text-amber-400/80"}>{label}</span>
      <b className="text-slate-100">{value}</b>
    </span>
  );
}
